import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, compose } from 'react-apollo';
import g11n from 'hudl-g11n';
import localforage from 'common/localforage';
import { showToast, dismissToast } from 'common/actions';
import { getConsentSummary, isPermissionOn, PERMISSION_KEYS } from '../../../utility/feature-access-utils';
import sendRequestFeatureAccessEmailMutation from '../../../graphql/mutations/send-request-feature-access-email';
import sendConsentReminderEmailMutation from '../../../graphql/mutations/send-consent-reminder-email';
import ConsentBanner from './consent-banner';

const { FormattedMessage } = g11n.ReactIntl;

const FEATURE_ACCESS_KEY = 'profiles.accountSettings.privacyPage.featureAccess';
const BANNER_KEY = 'profiles.consentBanner';

// The permissions this banner reports on, and the copy namespace for each combination of them being off
const BANNER_PERMISSION_KEYS = [PERMISSION_KEYS.PROFILE_IS_PUBLIC, PERMISSION_KEYS.CREATE_HIGHLIGHT];
const COPY_KEY_BY_LOCKED = {
  [PERMISSION_KEYS.PROFILE_IS_PUBLIC]: 'publicProfile',
  [PERMISSION_KEYS.CREATE_HIGHLIGHT]: 'createHighlight',
  [BANNER_PERMISSION_KEYS.join()]: 'both',
};

// The banner's permissions that are off; empty when selfPermissions is null (the lookup failed) so we never guess
function getLockedKeys(featureAccess, permissionKeys = BANNER_PERMISSION_KEYS) {
  const { selfPermissions } = featureAccess || {};
  if (!Array.isArray(selfPermissions)) {
    return [];
  }
  return permissionKeys.filter(key => !isPermissionOn(selfPermissions, key));
}

export class ConsentBannerContainer extends React.Component {
  constructor(props) {
    super(props);
    // null until the persisted dismissal has loaded, so a dismissed banner never flashes
    this.state = { dismissed: null, loading: false };
    this.handleDismiss = this.handleDismiss.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    this.loadDismissed();
  }

  componentDidUpdate(prevProps) {
    // A different set of locked permissions is a different banner, so it gets its own dismissal
    if (this.getDismissedKey(prevProps) !== this.getDismissedKey()) {
      this.setState({ dismissed: null }); // eslint-disable-line react/no-did-update-set-state
      this.loadDismissed();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  getDismissedKey(props = this.props) {
    return `${props.currentUserId}-consent-banner-${getLockedKeys(props.featureAccess, props.permissionKeys).join('-')}-dismissed`;
  }

  loadDismissed() {
    const key = this.getDismissedKey();
    localforage.getItem(key)
      .catch(() => false) // storage unavailable (e.g. private mode): show the banner rather than hide it forever
      .then((dismissed) => {
        if (this.mounted && key === this.getDismissedKey()) {
          this.setState({ dismissed: !!dismissed });
        }
      });
  }

  toast(type, key, values) {
    this.props.showToast({
      type,
      message: g11n.format(`${FEATURE_ACCESS_KEY}.${key}`, values),
      onDismiss: this.props.dismissToast,
    });
  }

  handleDismiss() {
    // Dismissal just won't persist if storage is unavailable
    localforage.setItem(this.getDismissedKey(), true).catch(() => {});
    this.setState({ dismissed: true });
  }

  // A Pending consent record gets a reminder to the guardian; an Approved record whose permission is
  // still off gets a feature access request instead.
  handleConsentAction({ isApproved, approvedEmails, pendingEntry, lockedKeys }) {
    this.setState({ loading: true });
    const emailRequest = isApproved
      ? this.requestFeatureAccess(approvedEmails, lockedKeys)
      : this.sendConsentReminderEmail(pendingEntry);
    emailRequest.then(() => this.mounted && this.setState({ loading: false }));
  }

  // With both permissions locked we omit the key, which the server treats as an "all features" request,
  // so the guardian gets one email instead of one per feature.
  requestFeatureAccess(approvedEmails, lockedKeys) {
    const { featureAccess } = this.props;
    const input = { subjectEmail: featureAccess.userEmail };
    if (lockedKeys.length === 1) {
      [input.permissionKey] = lockedKeys;
    }
    return this.props.sendRequestFeatureAccessEmail({ variables: { input } })
      .then((result) => {
        const sent = result && result.data && result.data.sendRequestFeatureAccessEmail;
        if (sent) {
          this.toast('confirmation', 'requestAccessSentToast', { parentEmail: approvedEmails });
        } else {
          this.toast('critical', 'requestAccessTooRecentToast');
        }
      })
      .catch(() => this.toast('critical', 'requestAccessErrorToast'));
  }

  sendConsentReminderEmail(pendingEntry) {
    return this.props.sendConsentReminderEmail({ variables: { consentStatusId: pendingEntry.userConsentStatusId } })
      .then(() => this.toast('confirmation', 'reminderSentToast', { parentEmail: pendingEntry.providerEmail }))
      .catch((error) => {
        // The reminder endpoint throws (rather than returning false) when rate limited
        if (error && error.message && error.message.indexOf('too recently') !== -1) {
          this.toast('critical', 'reminderTooRecentToast');
        } else {
          this.toast('critical', 'reminderErrorToast');
        }
      });
  }

  render() {
    const { currentUserId, userId, featureAccess, permissionKeys } = this.props;
    const { dismissed, loading } = this.state;
    const { ageTier, consentStatuses } = featureAccess || {};

    if (dismissed !== false || !currentUserId || currentUserId !== userId) {
      return null;
    }
    const lockedKeys = getLockedKeys(featureAccess, permissionKeys);
    if (!ageTier || ageTier === 'Adult' || lockedKeys.length === 0) {
      return null;
    }

    const { isApproved, approvedEmails, pendingEntry } = getConsentSummary(consentStatuses);
    if (!isApproved && !pendingEntry) {
      return null;
    }

    const providerEmail = isApproved ? approvedEmails : pendingEntry.providerEmail;
    const copyKey = `${BANNER_KEY}.${COPY_KEY_BY_LOCKED[lockedKeys.join()]}`;
    return (
      <ConsentBanner
        title={g11n.format(`${copyKey}.title`)}
        body={
          <FormattedMessage
            id={`${copyKey}.${isApproved ? 'approvedBody' : 'pendingBody'}`}
            values={{ providerEmail: <strong>{providerEmail}</strong> }}
          />
        }
        actionLabel={g11n.format(`${BANNER_KEY}.${isApproved ? 'requestAccessButton' : 'sendReminderButton'}`)}
        onActionClick={() => this.handleConsentAction({ isApproved, approvedEmails, pendingEntry, lockedKeys })}
        actionLoading={loading}
        onDismiss={this.handleDismiss}
      />
    );
  }
}

ConsentBannerContainer.propTypes = {
  currentUserId: PropTypes.string,
  userId: PropTypes.string,
  // Permissions to report on; defaults to all of BANNER_PERMISSION_KEYS
  permissionKeys: PropTypes.arrayOf(PropTypes.string),
  featureAccess: PropTypes.shape({
    ageTier: PropTypes.string,
    userEmail: PropTypes.string,
    selfPermissions: PropTypes.array,
    consentStatuses: PropTypes.array,
  }),
  sendRequestFeatureAccessEmail: PropTypes.func,
  sendConsentReminderEmail: PropTypes.func,
  showToast: PropTypes.func,
  dismissToast: PropTypes.func,
};

const mapStateToProps = (state) => ({
  featureAccess: state.featureAccess,
  currentUserId: state.currentUser && state.currentUser.userId,
  userId: state.user && state.user.userId,
});

export default compose(
  connect(mapStateToProps, { showToast, dismissToast }),
  graphql(sendRequestFeatureAccessEmailMutation, { name: 'sendRequestFeatureAccessEmail' }),
  graphql(sendConsentReminderEmailMutation, { name: 'sendConsentReminderEmail' }),
)(ConsentBannerContainer);
